/* eslint-disable react/jsx-props-no-spreading, react/no-danger */
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import * as AppPropTypes from '../../lib/PropTypes';
import { cleanExternalLinks } from '../../lib/utils';

import { useUrlGenerator } from '../../contexts/RoutesContext';
import RoundedButton from '../buttons/Rounded';
import TicketButton from '../buttons/Ticket';
import CalendarIcon from '../icons/Calendar';
import ListIcon from '../icons/List';

import styles from '../../../styles/headers/section.module.scss';

const propTypes = {
    edition: AppPropTypes.edition,
    slug: PropTypes.string,
    title: PropTypes.string,
    summary: PropTypes.string,
    description: PropTypes.string,
    withTicket: PropTypes.bool,
    ticketUrl: PropTypes.string,
    listType: PropTypes.string,
    isDark: PropTypes.bool,
    className: PropTypes.string,
};

const defaultProps = {
    edition: null,
    slug: null,
    title: null,
    summary: null,
    description: null,
    withTicket: false,
    ticketUrl: null,
    listType: null,
    isDark: false,
    className: null,
};

const SectionHeader = ({
    edition,
    slug,
    title,
    summary,
    description,
    withTicket,
    ticketUrl,
    listType,
    isDark,
    className,
}) => {
    const { locale } = useIntl();
    const route = useUrlGenerator();

    const listUrl = route('section_with_type_and_edition', {
        section: slug,
        list_type: locale === 'fr' ? 'liste' : 'list',
        edition: edition !== null ? edition.handle : null,
    });

    const calendarUrl = route('section_with_type_and_edition', {
        section: slug,
        list_type: locale === 'fr' ? 'calendrier' : 'calendar',
        edition: edition !== null ? edition.handle : null,
    });

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [className]: className !== null,
                    [styles.isDark]: isDark,
                },
            ])}
        >
            <div className={styles.inner}>
                <div className={styles.content}>
                    {summary !== null ? <h4 className={styles.surtitle}>{summary}</h4> : null}
                    {title !== null ? <h1 className={styles.title}>{title}</h1> : null}
                    {description !== null ? (
                        <p
                            className={styles.description}
                            dangerouslySetInnerHTML={{ __html: cleanExternalLinks(description) }}
                        />
                    ) : null}
                </div>

                <div className={styles.actions}>
                    <nav className={styles.buttons}>
                        {withTicket && ticketUrl !== null ? (
                            <TicketButton className={styles.ticketButton} href={ticketUrl} external>
                                <FormattedMessage
                                    defaultMessage="Buy the package"
                                    description="Button label"
                                />
                            </TicketButton>
                        ) : null}
                        <div className={styles.layoutButtons}>
                            <RoundedButton
                                href={listUrl}
                                iconPosition="left"
                                icon={<ListIcon />}
                                className={classNames([
                                    styles.button,
                                    { [styles.active]: listType !== 'events' },
                                ])}
                            >
                                <FormattedMessage
                                    defaultMessage="List"
                                    description="button label"
                                />
                            </RoundedButton>
                            <RoundedButton
                                href={calendarUrl}
                                iconPosition="left"
                                icon={<CalendarIcon />}
                                className={classNames([
                                    styles.calendarButton,
                                    styles.button,
                                    { [styles.active]: listType === 'events' },
                                ])}
                            >
                                <FormattedMessage
                                    defaultMessage="Calendar"
                                    description="button label"
                                />
                            </RoundedButton>
                        </div>
                    </nav>
                </div>
            </div>
        </div>
    );
};
SectionHeader.propTypes = propTypes;
SectionHeader.defaultProps = defaultProps;

export default SectionHeader;
